import { useCallback, useMemo } from 'react';
import { AlertTriangle, Download, FileText, Landmark, Receipt, ScrollText } from 'lucide-react';
import { AnnualTimelineRow, DebtEvent, DebtRecord, Property, PropertyDebtLink } from '../types';
import { buildModel } from '../services/financials';
import { buildDebtSnapshotForProperty, derivePropertyHoldPeriodYears } from '../services/debt';
import { calculateEstimatedDepreciation } from '../services/tax';
import { formatCurrency0 } from '../utils/format';

interface TaxReadinessViewProps {
  properties: Record<string, Property>;
  debts: Record<string, DebtRecord>;
  debtLinks: PropertyDebtLink[];
  debtEvents: DebtEvent[];
  selectedYear: number;
  onSelectYear: (year: number) => void;
}

type PropertyTaxRow = {
  propertyId: string;
  propertyName: string;
  address: string;
  sourceLabel: string;
  rent: number;
  expenses: number;
  interestPaid: number;
  debtService: number;
  depreciation: number | null;
  depreciationNeedsEstimate: boolean;
  depreciationAssumptionLabel: string;
  netBeforeDepreciation: number;
  estimatedTaxableIncome: number | null;
  notes: string;
};

const TaxReadinessView = (props: TaxReadinessViewProps) => {
  const { properties, debts, debtLinks, debtEvents, selectedYear, onSelectYear } = props;

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const currentYear = new Date().getFullYear();
    years.add(currentYear);

    Object.values(properties).forEach((property) => {
      const baseYear = property.baseYear || currentYear;
      years.add(baseYear);
      const debtSnapshot = buildDebtSnapshotForProperty(property.id, properties, debts, debtLinks, debtEvents);
      const holdYears = derivePropertyHoldPeriodYears(debtSnapshot);
      for (let year = baseYear; year < baseYear + holdYears; year += 1) {
        years.add(year);
      }

      (property.historicalActuals || []).forEach((actual) => years.add(actual.year));
    });

    return [...years].sort((left, right) => right - left);
  }, [debtEvents, debtLinks, debts, properties]);

  const propertyRows = useMemo(() => {
    return Object.values(properties).flatMap((property) => {
      const activeInputs = property.currentYearInputs || property.inputs;
      const debtSnapshot = buildDebtSnapshotForProperty(property.id, properties, debts, debtLinks, debtEvents);
      const modelResult = buildModel(
        activeInputs,
        property.riskProfile,
        debtSnapshot,
        { baseYear: property.baseYear, historicalActuals: property.historicalActuals },
        { modelEvents: property.modelEvents || [] }
      );
      const selectedYearRow = modelResult.annualTimeline.find((row: AnnualTimelineRow) => row.calendarYear === selectedYear);

      if (!selectedYearRow) return [];
      const depreciationEstimate = calculateEstimatedDepreciation(property, selectedYear);
      const netBeforeDepreciation = (selectedYearRow.grossRent || 0) - (selectedYearRow.expenses || 0) - (selectedYearRow.interestPaid || 0);

      // We use the existing annual timeline as the tax-readiness backbone so this
      // page reflects the same saved actuals and debt schedule already driving analysis.
      return [{
        propertyId: property.id,
        propertyName: property.name || 'Untitled Property',
        address: property.address || 'Address not set',
        sourceLabel: selectedYearRow.rowType === 'actual' ? 'Actual record' : selectedYearRow.rowType === 'current' ? 'Current model year' : 'Forecast year',
        rent: selectedYearRow.grossRent || 0,
        expenses: selectedYearRow.expenses || 0,
        interestPaid: selectedYearRow.interestPaid || 0,
        debtService: selectedYearRow.debtService || 0,
        depreciation: depreciationEstimate.annualDepreciation,
        depreciationNeedsEstimate: depreciationEstimate.needsEstimate,
        depreciationAssumptionLabel: depreciationEstimate.assumptionLabel,
        netBeforeDepreciation,
        estimatedTaxableIncome: depreciationEstimate.annualDepreciation === null
          ? null
          : netBeforeDepreciation - depreciationEstimate.annualDepreciation,
        notes: selectedYearRow.notes || ''
      } satisfies PropertyTaxRow];
    });
  }, [debtEvents, debtLinks, debts, properties, selectedYear]);

  const totals = useMemo(() => (
    propertyRows.reduce((summary: {
      rent: number;
      expenses: number;
      interestPaid: number;
      debtService: number;
      depreciation: number;
      netBeforeDepreciation: number;
      estimatedTaxableIncome: number;
      estimatedTaxableIncomeCount: number;
    }, row: PropertyTaxRow) => ({
      rent: summary.rent + row.rent,
      expenses: summary.expenses + row.expenses,
      interestPaid: summary.interestPaid + row.interestPaid,
      debtService: summary.debtService + row.debtService,
      depreciation: summary.depreciation + (row.depreciation || 0),
      netBeforeDepreciation: summary.netBeforeDepreciation + row.netBeforeDepreciation,
      estimatedTaxableIncome: summary.estimatedTaxableIncome + (row.estimatedTaxableIncome || 0),
      estimatedTaxableIncomeCount: summary.estimatedTaxableIncomeCount + (row.estimatedTaxableIncome === null ? 0 : 1)
    }), {
      rent: 0,
      expenses: 0,
      interestPaid: 0,
      debtService: 0,
      depreciation: 0,
      netBeforeDepreciation: 0,
      estimatedTaxableIncome: 0,
      estimatedTaxableIncomeCount: 0
    })
  ), [propertyRows]);

  const csvRows = useMemo(() => {
    const header = ['Property', 'Address', 'Source', 'Rent', 'Expenses', 'Interest Paid', 'Depreciation', 'Net Before Depreciation', 'Estimated Taxable Income', 'Notes'];
    const rows = propertyRows.map((row: PropertyTaxRow) => [
      row.propertyName,
      row.address,
      row.sourceLabel,
      row.rent.toFixed(2),
      row.expenses.toFixed(2),
      row.interestPaid.toFixed(2),
      row.depreciation?.toFixed(2) ?? 'Estimate needed',
      row.netBeforeDepreciation.toFixed(2),
      row.estimatedTaxableIncome?.toFixed(2) ?? 'Estimate needed',
      row.notes
    ]);

    return [header, ...rows]
      .map((row: Array<string>) => row.map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
  }, [propertyRows]);

  const handleExportCsv = useCallback(() => {
    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `wrennen-tax-readiness-${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [csvRows, selectedYear]);

  const hasData = propertyRows.length > 0;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">Tax Readiness</p>
          <h1 className="mt-2 text-[2rem] font-semibold tracking-tight text-slate-900">Organize records for year-end review</h1>
          <p className="mt-3 max-w-3xl text-[15px] leading-7 text-slate-500">
            This is a light bookkeeping layer built from your saved property actuals, debt schedules, and modeled year data.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-40">
            <label className="mb-2 block text-sm font-medium text-slate-500">Tax Year</label>
            <select
              value={selectedYear}
              onChange={(e: any) => onSelectYear(Number((e.target as HTMLSelectElement).value))}
              className="w-full rounded-2xl border border-slate-700/70 bg-slate-900/85 px-4 py-3 text-sm font-semibold text-slate-100 outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleExportCsv}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <Download size={16} /> Export Summary
          </button>
        </div>
      </div>

      <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-white p-3 text-amber-600">
            <AlertTriangle size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-700">Important legal notice</p>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-amber-700/90">
              <li>This dashboard helps organize rental records. It does not replace a tax professional.</li>
              <li>Historical rows use saved actuals when available; otherwise they reflect the current model year or forecast.</li>
              <li>Depreciation estimates are informational and may differ from your actual tax depreciation schedule. Confirm basis, land allocation, improvements, and placed-in-service dates with a tax professional.</li>
              <li>Depreciation basis should be maintained on each property record so this page can stay focused on reporting.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: 'Rent Recorded', value: formatCurrency0(totals.rent), accent: 'text-green-600', icon: <Landmark size={18} />, note: `${selectedYear} income across included properties` },
          { label: 'Operating Expenses', value: formatCurrency0(totals.expenses), accent: 'text-rose-600', icon: <Receipt size={18} />, note: 'Taxes, insurance, HOA, maintenance, utilities' },
          { label: 'Interest Paid', value: formatCurrency0(totals.interestPaid), accent: 'text-slate-900', icon: <ScrollText size={18} />, note: 'Captured when the annual debt schedule provides it' },
          { label: 'Estimated Depreciation', value: totals.depreciation > 0 ? formatCurrency0(totals.depreciation) : 'Estimate needed', accent: 'text-amber-600', icon: <FileText size={18} />, note: 'Estimated straight-line rental depreciation' },
          { label: 'Net Before Depreciation', value: formatCurrency0(totals.netBeforeDepreciation), accent: totals.netBeforeDepreciation >= 0 ? 'text-green-600' : 'text-rose-600', icon: <FileText size={18} />, note: 'Informational income before depreciation' },
          { label: 'Estimated Taxable Income', value: totals.estimatedTaxableIncomeCount > 0 ? formatCurrency0(totals.estimatedTaxableIncome) : 'Estimate needed', accent: totals.estimatedTaxableIncome >= 0 ? 'text-amber-600' : 'text-rose-600', icon: <FileText size={18} />, note: 'Estimated and informational only' },
          { label: 'Properties Included', value: String(propertyRows.length), accent: 'text-slate-900', icon: <Landmark size={18} />, note: `Properties contributing data for ${selectedYear}` }
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium text-slate-500">{card.label}</p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-500">{card.icon}</div>
            </div>
            <p className={`mt-5 text-3xl font-semibold tracking-tight ${card.accent}`}>{card.value}</p>
            <p className="mt-2 text-sm leading-6 text-slate-500">{card.note}</p>
          </div>
        ))}
      </div>

      <div className="mb-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-slate-500">
            <FileText size={18} />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">Property Tax Summary</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{selectedYear} summary by property</h2>
          </div>
        </div>

        {!hasData ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
            <p className="text-2xl font-semibold text-slate-900">No property data for {selectedYear}</p>
            <p className="mt-2 text-sm text-slate-500">
              Add historical actuals or save current property operating inputs to populate this summary.
            </p>
          </div>
        ) : (
          <div className="saas-table-shell">
            <div className="grid grid-cols-[1.6fr_repeat(6,0.85fr)] gap-4 border-b border-slate-200 px-5 py-4 text-sm font-medium text-slate-500">
              <div>Property</div>
              <div className="text-left">Rent</div>
              <div className="text-left">Expenses</div>
              <div className="text-left">Interest</div>
              <div className="text-left">Depreciation</div>
              <div className="text-left">Net Before Dep.</div>
              <div className="text-left">Est. Taxable Income</div>
            </div>

            {propertyRows.map((row: PropertyTaxRow) => (
              <div key={row.propertyId} className="grid grid-cols-[1.6fr_repeat(6,0.85fr)] gap-4 border-b border-slate-200 px-5 py-5 last:border-b-0">
                <div className="min-w-0 pr-4">
                  <p className="truncate text-base font-semibold text-slate-900">{row.propertyName}</p>
                  <p className="mt-1 truncate text-sm text-slate-400">{row.address}</p>
                  <p className="mt-2 text-sm text-slate-500">{row.sourceLabel}</p>
                  <p className="mt-2 text-xs text-amber-600">{row.depreciationAssumptionLabel}</p>
                </div>
                <div className="text-left text-base font-semibold text-slate-900">{formatCurrency0(row.rent)}</div>
                <div className="text-left text-base font-semibold text-rose-600">{formatCurrency0(row.expenses)}</div>
                <div className="text-left text-base font-semibold text-slate-900">{formatCurrency0(row.interestPaid)}</div>
                <div className="text-left text-base font-semibold text-amber-600">{row.depreciation === null ? 'Estimate needed' : formatCurrency0(row.depreciation)}</div>
                <div className={`text-left text-base font-semibold ${row.netBeforeDepreciation >= 0 ? 'text-green-600' : 'text-rose-600'}`}>{formatCurrency0(row.netBeforeDepreciation)}</div>
                <div className={`text-left text-base font-semibold ${row.estimatedTaxableIncome === null ? 'text-slate-400' : row.estimatedTaxableIncome >= 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                  {row.estimatedTaxableIncome === null ? 'Estimate needed' : formatCurrency0(row.estimatedTaxableIncome)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-[1.75rem] border border-slate-700/70 bg-slate-900/75 p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">What comes next</p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-[1.25rem] border border-slate-700/60 bg-slate-950/50 p-4">
            <p className="text-base font-semibold text-slate-900">Vendor ledger</p>
            <p className="mt-2 text-sm text-slate-500">Track contractor and service payments so the tax view can flag 1099 thresholds automatically.</p>
          </div>
          <div className="rounded-[1.25rem] border border-slate-700/60 bg-slate-950/50 p-4">
            <p className="text-base font-semibold text-slate-900">Expense categories</p>
            <p className="mt-2 text-sm text-slate-500">Move from one annual maintenance number into categorized records without abandoning the current model.</p>
          </div>
          <div className="rounded-[1.25rem] border border-slate-700/60 bg-slate-950/50 p-4">
            <p className="text-base font-semibold text-slate-900">Tax packet export</p>
            <p className="mt-2 text-sm text-slate-500">Package property summaries, debt interest, and supporting notes into a year-end handoff for your CPA.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxReadinessView;
